// Audio the live assistant's capture sends: 16 kHz, mono, 16-bit signed
// little-endian PCM, in frames of about 200 ms. Pure functions, so the rules
// that decide what leaves a browser are testable without one.

export const PCM = {
  sampleRate: 16_000,
  /** Samples per frame sent (200 ms). */
  frameSamples: 3200,
  /** Below this RMS (full scale 1) a frame is treated as silence. */
  silenceRms: 0.008,
  /** Frames still sent after the last loud one, so word endings are not cut. */
  hangoverFrames: 5,
} as const;

/** Linear resampling of mono float samples. */
export function resampleLinear(input: Float32Array, from: number, to: number): Float32Array {
  if (from === to || input.length === 0) return input;
  const ratio = from / to;
  const length = Math.max(1, Math.floor(input.length / ratio));
  const out = new Float32Array(length);
  for (let i = 0; i < length; i++) {
    const pos = i * ratio;
    const lo = Math.floor(pos);
    const hi = Math.min(lo + 1, input.length - 1);
    const frac = pos - lo;
    out[i] = (input[lo] ?? 0) * (1 - frac) + (input[hi] ?? 0) * frac;
  }
  return out;
}

/** Float samples in [-1, 1] to signed 16-bit PCM, clamped. */
export function toPcm16(input: Float32Array): Int16Array {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i] ?? 0));
    out[i] = s < 0 ? Math.round(s * 0x8000) : Math.round(s * 0x7fff);
  }
  return out;
}

export function rms(input: Float32Array): number {
  if (input.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < input.length; i++) sum += (input[i] ?? 0) * (input[i] ?? 0);
  return Math.sqrt(sum / input.length);
}

/** Decides which frames are worth sending: loud ones, and a short tail after
 * the last. A silent room sends nothing, which is what keeps the cost of a
 * quiet call near zero. */
export class VoiceGate {
  private tail = 0;

  /** True when this frame should be sent. */
  pass(frame: Float32Array): boolean {
    if (rms(frame) >= PCM.silenceRms) {
      this.tail = PCM.hangoverFrames;
      return true;
    }
    if (this.tail > 0) {
      this.tail -= 1;
      return true;
    }
    return false;
  }

  reset(): void {
    this.tail = 0;
  }
}

// ---- Audio sent on another person's behalf ------------------------------------
// While a recording is quiet the room has told no guest anything, so no guest's
// browser sends its own audio. The host's page hears every other person from
// the call itself and sends each one's frames tagged with who they are: the
// bytes "BRZ1", one byte for the id's length, the id, a pad byte when needed to
// keep the samples on an even offset, then the same 16-bit PCM an untagged
// frame carries. The Room takes a tagged frame from the host's seat alone.

const TAG = [0x42, 0x52, 0x5a, 0x31] as const;
const MAX_TAG_ID = 64;

/** `pcm` prefixed with the speaker it belongs to. Null when the id cannot be carried. */
export function packTagged(participantId: string, pcm: ArrayBuffer): ArrayBuffer | null {
  if (participantId.length === 0 || participantId.length > MAX_TAG_ID) return null;
  // Participant ids are plain ASCII; anything else cannot be carried.
  const id = new Uint8Array(participantId.length);
  for (let i = 0; i < participantId.length; i++) {
    const c = participantId.charCodeAt(i);
    if (c > 0x7f) return null;
    id[i] = c;
  }
  const head = (TAG.length + 1 + id.length + 1) & ~1;
  const out = new Uint8Array(head + pcm.byteLength);
  out.set(TAG, 0);
  out[TAG.length] = id.length;
  out.set(id, TAG.length + 1);
  out.set(new Uint8Array(pcm), head);
  return out.buffer;
}

/** The speaker and samples of a tagged frame, or null when it is not one. */
export function unpackTagged(frame: ArrayBuffer): { readonly participantId: string; readonly pcm: ArrayBuffer } | null {
  const bytes = new Uint8Array(frame);
  if (bytes.length < TAG.length + 2) return null;
  for (let i = 0; i < TAG.length; i++) if (bytes[i] !== TAG[i]) return null;
  const idLen = bytes[TAG.length] ?? 0;
  if (idLen === 0 || idLen > MAX_TAG_ID) return null;
  const head = (TAG.length + 1 + idLen + 1) & ~1;
  if (bytes.length < head) return null;
  let participantId = "";
  for (let i = 0; i < idLen; i++) participantId += String.fromCharCode(bytes[TAG.length + 1 + i] ?? 0);
  return { participantId, pcm: frame.slice(head) };
}
